import { Outcome } from "@iyk/vf-forms"
import { Analytics, useAnalytics } from "@iyk/vf-observability/react"
import { Playlist, type DownloadFn, type RequestDownloadFn } from "@iyk/vf-ui"
import * as React from "react"
import { parsePath, useLocation } from "react-router"
import { GlobalRoutes, type Route } from "../../../react-router/routes.ts"
import { useLang } from "../../../react-router/use-lang.ts"
import { ActivityEvent } from "../../activity/activity.ts"
import { useActivity } from "../../activity/use-activity.tsx"
import type { DownloadOutcome } from "../routes/download.ts"
import type { RequestDownloadOutcome } from "../routes/request-download.ts"

export function PlaylistProvider({ children, user }: PlaylistProvider.Props) {
  const analytics = useAnalytics()
  const activity = useActivity()
  const lang = useLang()

  const downloadFrom = useDownloadFrom({
    MERCH_ITEM: () => GlobalRoutes.merch.landing,
    PROFILE: () => GlobalRoutes.profile
  })

  const handleTrackPlay = (trackId: string, context?: "playlist" | "auto" | "manual" | "skip") => {
    analytics.track(Analytics.Event.TrackPlayed({ trackId, context }))
    activity.track(ActivityEvent.TrackPlayed({ trackId }))
  }

  const handleTrackEnd = (trackId: string) => analytics.track(Analytics.Event.TrackEnded({ trackId }))

  const handleTrackSkip = (trackId: string) => analytics.track(Analytics.Event.TrackSkipped({ trackId }))

  const download: DownloadFn = async (playlistId) => {
    analytics.track(Analytics.Event.PlaylistDownloadClicked({ playlistId }))

    if (!downloadFrom) {
      return {
        success: false,
        error: "No download type found"
      }
    }

    const response = await fetch(GlobalRoutes.playlist.download({ playlistId, type: downloadFrom }), {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    })

    if (!response.ok) {
      return {
        success: false,
        error: "Failed to download playlist"
      }
    }

    const data = (await response.json()) as DownloadOutcome

    if (Outcome.isError(data)) {
      return {
        success: false,
        error: data.errors.error
      }
    }

    analytics.track(Analytics.Event.PlaylistDownloaded({ playlistId }))
    return {
      success: true,
      downloadUrl: data.data.downloadUrl
    }
  }

  const requestDownload: RequestDownloadFn = async (playlistId, email) => {
    if (!downloadFrom) {
      return {
        success: false,
        error: "No download type found"
      }
    }

    const response = await fetch(GlobalRoutes.playlist.requestDownload({ playlistId, type: downloadFrom }), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    })

    if (!response.ok) {
      return {
        success: false,
        error: "Failed to request download"
      }
    }

    const data = (await response.json()) as RequestDownloadOutcome

    if (Outcome.isError(data)) {
      return {
        success: false,
        error: data.errors.error
      }
    }

    analytics.track(Analytics.Event.PlaylistDownloadLinkRequested({ playlistId, email }))
    return {
      success: true
    }
  }

  return (
    <Playlist.Provider
      onTrackPlay={handleTrackPlay}
      onTrackEnd={handleTrackEnd}
      onTrackSkip={handleTrackSkip}
      user={{
        email: user?.email
      }}
      download={download}
      requestDownload={requestDownload}
      lang={lang}
    >
      {children}
    </Playlist.Provider>
  )
}

namespace PlaylistProvider {
  export type User = {
    id: string
    email: string
  }

  export type Props = {
    children: React.ReactNode
    user?: User
  }
}

/**
 * This function is used to determine the download type from the current path.
 * If the current path is a match for any of the possible routes, the type is returned.
 * @param possibleRoutes - A record of possible routes and their parameters.
 * @returns The type of the download or null if no match is found.
 */
function useDownloadFrom<T extends string>(
  possibleRoutes: Record<T, (params: Record<string, string>) => Route>
): T | null {
  const location = useLocation()
  const currentPath = location.pathname

  for (const type in possibleRoutes) {
    const path = possibleRoutes[type]({}) // We don't care about the search params here
    const { pathname } = parsePath(path)
    if (currentPath === pathname) {
      return type as T
    }
  }

  return null
}
